import { getClientSource } from '../utils/getClientSource';
import { BASE_URL } from './baseURL';

const ENDPOINTS = {
  AMAN_QUOTE: '/Aman/quote',
  AMAN_PAY: '/policy/pay',
  AMAN_REVIEW_EMAIL: '/aman/SendAmanReviewEmail'
};

const makeAPICall = async (endpoint, data, options = {}) => {
  const { timeout = 30000, useAuth = false, token = null } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log(`=== Making AMAN API call ===`);
    console.log(`Endpoint: ${BASE_URL}${endpoint}`);
    console.log(`Payload:`, JSON.stringify(data, null, 2));

    const headers = {
      'Content-Type': 'application/json',
    };

    if (useAuth && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const startTime = Date.now();
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const endTime = Date.now();
    console.log(`API call completed in ${endTime - startTime}ms`);
    console.log(`Response status: ${response.status}`);

    if (!response.ok) {
      console.error(`API Error Response:`, response.status, response.statusText);
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        console.error(`Error data:`, errorData);
        if (errorData.ErrorMessageEn) {
          errorMessage = errorData.ErrorMessageEn;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log(`=== AMAN API Success Response ===`);
    console.log(JSON.stringify(result, null, 2));

    return { success: true, data: result };

  } catch (error) {
    clearTimeout(timeoutId);

    console.error(`=== AMAN API Error ===`);
    console.error(`Error name: ${error.name}`);
    console.error(`Error message: ${error.message}`);

    if (error.name === 'AbortError') {
      return {
        success: false,
        error: `Request timed out after ${timeout}ms`,
        isTimeout: true
      };
    }

    const isNetworkError = !navigator.onLine || error.name === 'TypeError' || error.message.includes('fetch');

    return {
      success: false,
      error: error.message || 'API call failed',
      isNetworkError: isNetworkError,
      isTimeout: error.name === 'AbortError'
    };
  }
};

export const getAmanQuote = async (quoteData, options = {}) => {
  try {
    const { timeout = 30000, token = null } = options;

    const {
      isLoggedIn,
      partyCode,
      cpr,
      fullName,
      dateOfBirth,
      email,
      phoneNumber,
      plan,
      member
    } = quoteData;

    const payload = {
      plan: plan,
      placeOfResidence: "Bahrain",
      member: {
        cpr: member.cpr || "",
        firstName: member.firstName || "",
        lastName: member.lastName || "",
        dateOfBirth: member.dateOfBirth || "",
        gender: member.gender || "",
        nationality: member.nationality || "",
        countryOfResidence: member.countryOfResidence || "BH",
        maritalStatus: member.maritalStatus || "SINGLE",
        passportNo: member.passportNo || ""
      }
    };

    if (isLoggedIn) {
        // Logged-in users are identified by the auth token. Only attach a real
        // partyCode — never the "FALCON" guest fallback — and never send the
        // guest contact fields.
        if (partyCode && partyCode !== "FALCON") {
          payload.partyCode = partyCode;
        }
      } else {
        payload.cpr = cpr || "";
        payload.fullName = fullName || "";
        payload.dateOfBirth = dateOfBirth || "";
        payload.email = email || "";
        payload.phoneNumber = phoneNumber || "";
      }

    console.log('=== AMAN Quote Request ===');
    console.log('Payload:', JSON.stringify(payload, null, 2));

    const result = await makeAPICall(ENDPOINTS.AMAN_QUOTE, payload, {
      timeout,
      useAuth: !!token,
      token
    });

    if (result.success) {
      console.log('=== AMAN Quote Success ===');
      const quoteNumber = result.data?.PolicyNumber || 
                          result.data?.QuoteDetails?.QuoteNumber || 
                          result.data?.QuoteNumber || 
                          result.data?.quoteNumber || 
                          "";
      console.log('Extracted quoteNumber:', quoteNumber);
      return {
        success: true,
        data: result.data,
        quoteNumber: quoteNumber,
        totalPremium: result.data?.TotalPremium || result.data?.totalPremium || 0,
        premium: result.data?.Premium || result.data?.premium || 0,
        tax: result.data?.Tax || result.data?.tax || 0,
        policyNumber: result.data?.PolicyNumber || result.data?.policyNumber || ""
      };
    }

    console.error('=== AMAN Quote Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to get AMAN quote',
      data: null
    };

  } catch (error) {
    console.error('AMAN quote exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to get AMAN quote',
      data: null
    };
  }
};

export const payAmanPolicy = async (paymentData, options = {}) => {
  try {
    const { timeout = 30000, token = null } = options;

    const payload = {
      PolicyNumber: paymentData.quoteNumber,
      Premium: paymentData.premium,
      Channel: getClientSource(),
    };

    if (paymentData.promoCode) {
      payload.PromoCode = paymentData.promoCode;
    }

    console.log('=== AMAN Pay Request ===');
    console.log('Payload:', JSON.stringify(payload, null, 2));

    const result = await makeAPICall(ENDPOINTS.AMAN_PAY, payload, {
      timeout,
      useAuth: !!token,
      token
    });

    if (result.success) {
      console.log('=== AMAN Pay Success ===');
      return {
        success: true,
        data: result.data,
        paymentLink: result.data?.Link || result.data?.link || result.data?.PaymentLink || ""
      };
    }

    console.error('=== AMAN Pay Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to process payment',
      data: null
    };

  } catch (error) {
    console.error('AMAN pay exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to process payment',
      data: null
    };
  }
};

export const sendAmanReviewEmail = async (emailData, options = {}) => {
  try {
    const { timeout = 30000 } = options;

    const payload = {
      UserName: emailData.userName || "",
      UserEmail: emailData.userEmail || "",
      UserMobile: emailData.userMobile || "",
      UserDob: emailData.userDob || "",
      Q1HeartConditions: emailData.q1HeartConditions || false,
      Q2HighBloodPressure: emailData.q2HighBloodPressure || false,
      Q3Diabetes: emailData.q3Diabetes || false,
      Q4RespiratoryDiseases: emailData.q4RespiratoryDiseases || false,
      Q5ThyroidDisorders: emailData.q5ThyroidDisorders || false,
      Q6HighCholesterol: emailData.q6HighCholesterol || false
    };

    console.log('=== AMAN Review Email Request ===');
    console.log('Payload:', JSON.stringify(payload, null, 2));

    const result = await makeAPICall(ENDPOINTS.AMAN_REVIEW_EMAIL, payload, {
      timeout,
      useAuth: false
    });

    if (result.success) {
      console.log('=== AMAN Review Email Success ===');
      return {
        success: true,
        data: result.data
      };
    }

    console.error('=== AMAN Review Email Failed ===');
    return {
      success: false,
      error: result.error || 'Failed to send review email',
      data: null
    };

  } catch (error) {
    console.error('AMAN review email exception:', error);
    return {
      success: false,
      error: error.message || 'Failed to send review email',
      data: null
    };
  }
};

const amanServices = {
  getAmanQuote,
  payAmanPolicy,
  sendAmanReviewEmail
};

export default amanServices;
